import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { apDungTuyChinh, docTuyChinh, luuTuyChinh } from '../styles/tuy-chinh';
import type { TuyChinh } from '../styles/tuy-chinh';

/**
 * Hai chủ đề chọn được.
 *
 * Chủ đề `vibrant` đã **bỏ** (ADR-UI-0007): bảng màu của nó bị đánh giá là xấu,
 * và nhu cầu "đổi màu cho khác đi" giờ do bộ tùy chỉnh phục vụ đúng hơn.
 */
export type Theme = 'light' | 'dark';

/** Khóa lưu chủ đề trong localStorage. Phải khớp script chặn FOUC ở index.html. */
export const THEME_STORAGE_KEY = 'foxai-theme';

const THEME_CLASSES: readonly Theme[] = ['dark'];

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  /** Đổi qua lại giữa nền sáng và nền tối. */
  toggleTheme: () => void;
  /** Tùy chỉnh màu nhấn và bo góc. */
  tuyChinh: TuyChinh;
  setTuyChinh: (tc: TuyChinh) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Đọc chủ đề đang nằm trên <html>, do script chặn FOUC đặt trước khi vẽ. */
function readThemeFromDocument(): Theme {
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
}

/** Đặt class chủ đề lên `<html>` và nhớ lựa chọn. */
function ghiChuDe(next: Theme): void {
  const root = document.documentElement;
  root.classList.remove(...THEME_CLASSES);
  if (next !== 'light') root.classList.add(next);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Chế độ riêng tư hoặc bị chặn lưu: vẫn đổi được chủ đề, chỉ không nhớ.
  }
}

/**
 * Chủ đề là một **class trên `<html>`**: không có class (sáng) hoặc `.dark`.
 * Không dùng thuộc tính `[data-theme]` (§5.14).
 *
 * Provider đọc lại class đã có trên `<html>` lúc mount để state React khớp với
 * thứ đang hiển thị, nên không có nhấp nháy.
 *
 * Tùy chỉnh của người dùng (màu nhấn, bo góc) được ghi lên `<html>` dưới dạng
 * biến CSS inline — xem `styles/tuy-chinh.ts`.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readThemeFromDocument);
  const [tuyChinh, setTuyChinhState] = useState<TuyChinh>(docTuyChinh);

  /*
   * Áp lại tùy chỉnh mỗi khi nó đổi **hoặc** khi đổi chủ đề: thang primary của
   * nền sáng và nền tối ngược chiều nhau, nên đổi chủ đề mà không áp lại sẽ để
   * nguyên thang của chủ đề cũ.
   */
  useEffect(() => {
    apDungTuyChinh(tuyChinh, theme === 'dark');
  }, [tuyChinh, theme]);

  const setTheme = useCallback((next: Theme) => {
    ghiChuDe(next);
    setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = current === 'light' ? 'dark' : 'light';
      ghiChuDe(next);
      return next;
    });
  }, []);

  const setTuyChinh = useCallback((tc: TuyChinh) => {
    luuTuyChinh(tc);
    setTuyChinhState(tc);
  }, []);

  // Nếu người dùng đổi chủ đề hệ thống mà chưa từng chọn tay, đi theo hệ thống.
  useEffect(() => {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(THEME_STORAGE_KEY);
    } catch {
      return;
    }
    if (stored) return;

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (event: MediaQueryListEvent) => {
      const root = document.documentElement;
      root.classList.remove(...THEME_CLASSES);
      if (event.matches) root.classList.add('dark');
      setThemeState(event.matches ? 'dark' : 'light');
    };
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const value = useMemo(
    () => ({ theme, setTheme, toggleTheme, tuyChinh, setTuyChinh }),
    [theme, setTheme, toggleTheme, tuyChinh, setTuyChinh],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme phải được gọi bên trong <ThemeProvider>');
  return ctx;
}
